import type { FlightOfferSource } from '../flight_offer_source.js';
import type { FlightSearchCabin } from './flight_filter_panel.js';

/** Uma cabine que a política do viajante permite. `minDurationHours` = só em voos a
 *  partir de N horas ("executiva a partir de 6h", o `durationLimit` da policy-api). */
export type FlightCabinAllowance = {
  cabin: FlightSearchCabin;
  minDurationHours?: number;
};

/**
 * As cabines que a política do viajante permite (#6438), pela forma do
 * `FlightCabinPolicy` do domain. Ausente = a política não foi lida, e o seletor
 * segue como sempre (fail-open): nenhuma cabine é marcada.
 */
export type FlightCabinPolicy = { allowed: readonly FlightCabinAllowance[] };

/**
 * O que o seletor diz de uma cabine. `out` = fora da política; `from_hours` = permitida
 * só a partir de N horas de voo, e `met` diz se algum voo da lista em cena chega lá.
 * `null` = nada a dizer (sem política, ou cabine permitida sem condição).
 */
export type CabinPolicyMark = { kind: 'out' } | { kind: 'from_hours'; hours: number; met: boolean };

/** O tempo de voo de uma oferta: a soma dos segmentos da perna em cena (sem as
 *  conexões, que não são horas de voo). */
const flightMinutes = (offer: FlightOfferSource): number =>
  offer.segments.reduce((sum, s) => sum + (s.durationMinutes ?? 0), 0);

/** O voo mais longo da lista em cena — é o trecho que a condição por duração mede. */
export function longestFlightMinutes(offers: readonly FlightOfferSource[]): number {
  return offers.reduce((max, offer) => Math.max(max, flightMinutes(offer)), 0);
}

/**
 * A marca de política de uma cabine. A classe fora da política continua pesquisável
 * (decisão de produto, 27/10): a policy-api tem exceções por país e aeroporto que o
 * seletor não enxerga, então esconder a opção bloquearia quem a exceção libera.
 */
export function cabinPolicyMark(
  policy: FlightCabinPolicy | undefined,
  cabin: FlightSearchCabin,
  longestMinutes: number,
): CabinPolicyMark | null {
  if (!policy) return null;
  const allowance = policy.allowed.find((a) => a.cabin === cabin);
  if (!allowance) return { kind: 'out' };
  const hours = allowance.minDurationHours;
  if (!hours || hours <= 0) return null;
  return { kind: 'from_hours', hours, met: longestMinutes >= hours * 60 };
}
